import { Component, EventEmitter, OnInit, Output, ViewChild, ElementRef, Inject } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ConfigurationAccessGroupComplete, ConfigurationAccessService } from 'src/app/core/services/data/request/configuration-access.service';

import { forkJoin, Observable, of } from 'rxjs';
import { UserService } from 'src/app/core/services/data/request/user.service';
import { UserGroupService } from 'src/app/core/services/data/request/user-group.service';
import { ConfigurationService } from 'src/app/core/services/data/request/configuration.service';
import { UserDetailData } from 'src/app/models/user-detail-data.model';
import { catchError, debounceTime, first, map } from 'rxjs/operators';


interface UserGroup {
  id: number;
  name: string;
}

interface ConfigurationItem {
  uuid: string;
  name: string;
}

interface DialogData {
  isEdit: boolean;
  groupUuid?: string;
  qraDefaultConfigUuids?: string[];
}

@Component({
  selector: 'app-add-access-group-dialog',
  template: `
<div class="dialog-container">
  <h1 mat-dialog-title>{{ isEditMode ? 'Edit' : 'Add' }} Configuration Access Group</h1>

  <div mat-dialog-content>
    <form [formGroup]="accessGroupForm" class="form-layout">
      <!-- Group Name Input -->
      <mat-form-field class="full-width">
        <mat-label>Access Group Name</mat-label>
        <input matInput formControlName="name" required />
        <mat-error *ngIf="accessGroupForm.get('name').hasError('required')">
          Name is required
        </mat-error>
        <mat-error *ngIf="accessGroupForm.get('name').hasError('nameExists')">
          This name already exists. Please choose a different name.
        </mat-error>
        <mat-hint *ngIf="accessGroupForm.get('name').pending">
          Checking name availability...
        </mat-hint>
      </mat-form-field>

      <!-- Description Input -->
      <mat-form-field class="full-width">
        <mat-label>Description</mat-label>
        <textarea matInput formControlName="description" rows="2"></textarea>
      </mat-form-field>

      <div class="selection-controls">
        <!-- Users Selection -->
<mat-form-field appearance="outline" class="selection-field">
  <mat-label>
    {{ availableUsers?.length === 0 ? 'No Users Available to Assign' : 'Select Users' }}
  </mat-label>
  <mat-select formControlName="userIds" [disabled]="availableUsers?.length === 0" multiple>
    <mat-option *ngFor="let user of availableUsers" [value]="user.userId">
      {{ user.name }}
    </mat-option>
  </mat-select>
  <mat-hint *ngIf="availableUsers?.length === 0">No Users Available to Assign</mat-hint>
</mat-form-field>

        <!-- User Groups Selection (Conditional Rendering) -->
        <mat-form-field
          *ngIf="availableUserGroups?.length > 0"
          appearance="outline"
          class="selection-field"
        >
          <mat-label>Assign User Groups</mat-label>
          <mat-select formControlName="userGroupIds" multiple>
            <mat-option *ngFor="let group of availableUserGroups" [value]="group.id">
              {{ group.name }}
            </mat-option>
          </mat-select>
        </mat-form-field>
      </div>

      <!-- Configurations Selection -->
      <mat-form-field appearance="outline" class="full-width">
        <mat-label>Assign Configurations</mat-label>
        <mat-select
          formControlName="configurationUuids"
          [disabled]="availableConfigurations?.length === 0"
          multiple
        >
          <mat-option *ngFor="let config of availableConfigurations" [value]="config.uuid">
            {{ config.name }}
          </mat-option>
        </mat-select>
        <mat-hint *ngIf="availableConfigurations?.length === 0"
          >No Configurations Available to Assign</mat-hint
        >
      </mat-form-field>
    </form>
  </div>

  <!-- Dialog Actions -->
  <div mat-dialog-actions class="dialog-actions">
    <button mat-button [mat-dialog-close]="true" class="cancel-button">Close</button>
    <button
      mat-raised-button
      color="primary"
      [disabled]="accessGroupForm.invalid || accessGroupForm.pending"
      (click)="onSaveClick()"
    >
      Save
    </button>
  </div>

  <!-- Loading Overlay -->
  <div *ngIf="loading" class="loading-overlay">
    <mat-spinner diameter="40"></mat-spinner>
  </div>
</div>
`,
  styleUrls: ['./add-access-group-dialog.component.scss']
})

export class AddAccessGroupDialogComponent implements OnInit {
  @ViewChild('groupNameInput') groupNameInput: ElementRef;

  @Output() groupSaved = new EventEmitter<ConfigurationAccessGroupComplete>();

  accessGroupForm: FormGroup;
  isEditMode = false;
  groupUuid: string;
  
  qraDefaultConfigUuids: string[] = [];
  availableUsers: UserDetailData[] = [];
  availableUserGroups: UserGroup[] = [];
  availableConfigurations: ConfigurationItem[] = [];

  loading = false;

  constructor(
    private formBuilder: FormBuilder,
    public dialogRef: MatDialogRef<AddAccessGroupDialogComponent>,
    private accessService: ConfigurationAccessService,
    private userService: UserService,
    private userGroupService: UserGroupService,
    private configService: ConfigurationService,
    @Inject(MAT_DIALOG_DATA) public data: DialogData
  ) {
    this.isEditMode = data.isEdit;
    this.groupUuid = data.groupUuid || null;
    
    // Store QRA Default configurations if provided
    this.qraDefaultConfigUuids = data.qraDefaultConfigUuids || [];
    
    this.initForm();
  }

  private initForm(): void {
    this.accessGroupForm = this.formBuilder.group({
      name: ['', {
        validators: [Validators.required],
        asyncValidators: [this.uniqueNameValidator()],
        updateOn: 'blur'
      }],
      description: [''],
      userIds: [[]],
      userGroupIds: [[]],
      configurationUuids: [[]]
    });
  }

  private uniqueNameValidator(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const name = control.value;
      if (!name) {
        return of(null);
      }

      if (this.isEditMode && this.accessGroupForm &&
        this.originalGroupName === name) {
        return of(null);
      }

      return this.accessService.getAccessGroupByName(name)
        .pipe(
          debounceTime(300),
          map(group => {
            return { nameExists: true };
          }),
          catchError(() => {
            return of(null);
          }),
          first()
        );
    };
  }

  private originalGroupName: string;

  ngOnInit() {
    this.loading = true;
    forkJoin([
      this.userService.getAllActiveUserData(),
      this.userGroupService.getUserGroups(),
      this.configService.getAllConfigurationSummaries()
    ]).subscribe(
      ([users, userGroups, configurations]) => {
        
        this.availableUsers = users
          .filter(user => !user.roles.some(role => role.name === 'tenantadmin'))
          .sort((a, b) => a.name.localeCompare(b.name));
        
        this.availableUserGroups = userGroups
          .sort((a, b) => a.name.localeCompare(b.name));
        
        this.availableConfigurations = configurations
          .filter(config => !this.qraDefaultConfigUuids.includes(config.configurationId) || 
                           (this.isEditMode && 
                            this.groupUuid === "00000000-0000-0000-0000-000000000001"))
          .map(config => ({
            uuid: config.configurationId,
            name: config.configurationName
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
  
        if (this.isEditMode && this.groupUuid) {
          this.loadExistingGroup();
        }
  
        this.loading = false;
      },
      error => {
        console.error('Error loading data', error);
        this.loading = false;
      }
    );
  }

  private loadExistingGroup(): void {
    forkJoin([
      this.accessService.getAccessGroupByUuid(this.groupUuid),
      this.accessService.getUsersInAccessGroup(this.groupUuid),
      this.accessService.getUserGroupsInAccessGroup(this.groupUuid),
      this.accessService.getConfigurationsInAccessGroup(this.groupUuid)
    ]).subscribe(
      ([group, userIds, userGroupIds, configUuids]) => {
        // Store the original name for comparison in validator
        this.originalGroupName = group.name;

        const isQraDefaultGroup = group.name === 'QRA Default';
        
        const validConfigUuids = isQraDefaultGroup ? 
          configUuids : 
          configUuids.filter(uuid => !this.qraDefaultConfigUuids.includes(uuid));

        this.accessGroupForm.patchValue({
          name: group.name,
          description: group.description,
          userIds: userIds,
          userGroupIds: userGroupIds,
          configurationUuids: validConfigUuids
        });
      },
      error => {
        console.error('Error loading group data', error);
      }
    );
  }

  onSaveClick(): void {
    if (this.accessGroupForm.invalid) {
      return;
    }

    const formValue = this.accessGroupForm.value;
    const userIds = (formValue.userIds || []).filter(id => id !== null);
    const userGroupIds = (formValue.userGroupIds || []).filter(id => id !== null);
    const configurationUuids = (formValue.configurationUuids || []).filter(uuid => uuid !== null);

    const completeGroup: ConfigurationAccessGroupComplete = {
      group: {
        uuid: this.isEditMode ? this.groupUuid : null,
        name: formValue.name,
        description: formValue.description || ''
      },
      userIds: userIds,
      userGroupIds: userGroupIds,
      configurationUuids: configurationUuids
    };

    if (this.isEditMode) {
      this.accessService.updateCompleteAccessGroup(completeGroup).subscribe(
        result => {
          this.dialogRef.close(result);
        },
        error => {
          console.error('Error updating access group', error);
        }
      );
    } else {
      this.accessService.createCompleteAccessGroup(completeGroup).subscribe(
        result => {
          this.dialogRef.close(result);
        },
        error => {
          console.error('Error creating access group', error);
        }
      );
    }
  }
}